import { type Ref, ref, reactive, computed, type ComputedRef } from "vue";
import { defineStore } from "pinia";

import type { HospitalStatuses, StatusPriority } from "@/App/types/twiageTypes";
import type { TwiageRequestError } from "./../../App/services/httpClient/HttpClient";
import { DataState, InitialState, Loading, LoadingFailed, LoadingSucceeded } from "@/Pages/utils/DataState";
import { StatusesService } from "./services";

export type StatusToHandlePayload = {
  status: string | undefined;
  priority: StatusPriority | undefined;
};

export type StatusToHandle = {
  hospitalId?: string;
  payload: StatusToHandlePayload;
  id: string | undefined;
};

const createStatusToHandle = (): StatusToHandle => ({
  hospitalId: undefined,
  payload: {
    status: undefined,
    priority: undefined,
  },
  id: undefined,
});

export const useHospitalsStatusesStore = defineStore("hospitalsStatuses", () => {
  const hospitalsStatuses: Ref<HospitalStatuses[]> = ref([]);
  const getStatusesRequestState: Ref<DataState<void>> = ref(new InitialState());
  const createStatusRequestState: Ref<DataState<void>> = ref(new InitialState());
  const editStatusRequestState: Ref<DataState<void>> = ref(new InitialState());
  const removeStatusRequestState: Ref<DataState<void>> = ref(new InitialState());
  const statusBeingHandled: StatusToHandle = reactive(createStatusToHandle());
  const isStatusBeingHandled: Ref<boolean> = ref(false);
  const isStatusBeingRemoved: Ref<boolean> = ref(false);

  const isStatusRequestStateLoading: ComputedRef<boolean> = computed(
    () =>
      createStatusRequestState.value instanceof Loading ||
      editStatusRequestState.value instanceof Loading ||
      removeStatusRequestState.value instanceof Loading,
  );

  const setStatusBeingHandled = ({
    hospitalId,
    id,
    removing,
  }: {
    hospitalId: string;
    id?: string;
    removing?: boolean;
  }): void => {
    isStatusBeingHandled.value = true;
    if (removing) {
      isStatusBeingRemoved.value = true;
    }
    Object.assign(statusBeingHandled, { hospitalId, id });
  };

  const unsetStatusBeingHandled = (): void => {
    isStatusBeingHandled.value = false;
    isStatusBeingRemoved.value = false;
    Object.assign(statusBeingHandled, createStatusToHandle());
  };

  const getHospitalsAdministeredStatuses = async (): Promise<void> => {
    try {
      hospitalsStatuses.value = await StatusesService.getHospitalsAdministeredStatuses();
    } catch (error) {
      getStatusesRequestState.value = new LoadingFailed(
        (error as TwiageRequestError).message,
        "Failed to retrieve statuses from server",
      );
    }
  };

  const createHospitalStatus = async (): Promise<void> => {
    createStatusRequestState.value = new Loading();

    const {
      hospitalId,
      payload: { status, priority },
    } = statusBeingHandled;

    if (hospitalId && status && priority) {
      try {
        await StatusesService.createHospitalStatus({
          hospitalId,
          payload: { status, priority },
        });

        await getHospitalsAdministeredStatuses();
        createStatusRequestState.value = new LoadingSucceeded();
      } catch (error) {
        createStatusRequestState.value = new LoadingFailed(
          (error as TwiageRequestError).message,
          `Failed to create status`,
        );
      }

      unsetStatusBeingHandled();
    }
  };

  const editHospitalStatus = async (): Promise<void> => {
    editStatusRequestState.value = new Loading();

    const {
      hospitalId,
      id,
      payload: { status, priority },
    } = statusBeingHandled;

    if (hospitalId && id && status && priority) {
      try {
        await StatusesService.editHospitalStatusById({
          hospitalId,
          statusId: id,
          payload: { status, priority },
        });

        await getHospitalsAdministeredStatuses();
        editStatusRequestState.value = new LoadingSucceeded();
      } catch (error) {
        editStatusRequestState.value = new LoadingFailed(
          (error as TwiageRequestError).message,
          `Failed to edit status`,
        );
      }

      unsetStatusBeingHandled();
    }
  };

  const removeHospitalStatus = async (): Promise<void> => {
    removeStatusRequestState.value = new Loading();

    const { hospitalId, id } = statusBeingHandled;

    if (hospitalId && id) {
      try {
        await StatusesService.removeHospitalStatusById({
          hospitalId,
          statusId: id,
        });

        await getHospitalsAdministeredStatuses();
        removeStatusRequestState.value = new LoadingSucceeded();
      } catch (error) {
        removeStatusRequestState.value = new LoadingFailed(
          (error as TwiageRequestError).message,
          `Failed to remove status`,
        );
      }

      unsetStatusBeingHandled();
    }
  };

  const onSessionEnd = (): void => {
    hospitalsStatuses.value = [];
  };

  return {
    hospitalsStatuses,
    statusBeingHandled,
    isStatusBeingHandled,
    isStatusBeingRemoved,
    isStatusRequestStateLoading,
    getStatusesRequestState,
    createStatusRequestState,
    editStatusRequestState,
    removeStatusRequestState,
    setStatusBeingHandled,
    unsetStatusBeingHandled,
    getHospitalsAdministeredStatuses,
    createHospitalStatus,
    editHospitalStatus,
    removeHospitalStatus,
    onSessionEnd,
  };
});
